import React, { useState } from 'react';
import { ArrowRight, Search, FileCheck, Bell, Shield, Headphones, Sparkles, Award, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import CounterAnimation from './CounterAnimation';
import RegistrationModal from './RegistrationModal';
import AttorneyChatButton from './AttorneyChatButton';

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const services = [
    { icon: Search, text: 'Trademark Search' },
    { icon: FileCheck, text: 'Registration Filing' },
    { icon: Shield, text: 'Brand Protection' },
    { icon: Bell, text: 'Monitoring Services' },
    { icon: Headphones, text: 'Expert Legal Support' }
  ];

  return (
    <div className="relative">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm">
          <span>🎉 <span className="font-semibold">Trusted by thousands of businesses</span> - Secure your brand with professional trademark protection</span>
          <button 
            onClick={() => openModal('registration')}
            className="text-[#168356] hover:underline flex items-center gap-1 transition-colors"
          >
            Claim Offer <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Hero Content - Modern Gradient Style */}
      <div className="relative min-h-[700px] overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]">
        {/* Animated Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#168356]/20 via-transparent to-[#0a5938]/20 animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-[#168356]/10 to-transparent"></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#168356]/10 rounded-full blur-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-[#0a5938]/10 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-[#168356]/10 rounded-full blur-xl animate-bounce" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-white/5 rounded-full blur-lg animate-bounce" style={{ animationDuration: '4s' }}></div>

        {/* Floating Icons */}
        <div className="absolute top-32 right-1/4 animate-float" style={{ animationDelay: '0s' }}>
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#168356]" />
          </div>
        </div>
        <div className="absolute top-1/2 left-20 animate-float" style={{ animationDelay: '1s' }}>
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Award className="w-6 h-6 text-[#168356]" />
          </div>
        </div>
        <div className="absolute bottom-32 right-32 animate-float" style={{ animationDelay: '2s' }}>
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-[#168356]" />
          </div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `linear-gradient(#168356 1px, transparent 1px), linear-gradient(90deg, #168356 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-[#168356]/30 px-4 py-2 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#168356] animate-pulse" />
              <span className="text-sm font-medium text-white">
                Trusted by thousands of businesses
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-medium text-white mb-6 leading-tight">
              Secure your brand with
              <br />
              <span className="text-white font-bold bg-clip-text text-transparent">
                professional trademark protection
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              File your trademark with complete confidence. AI-based search technology, experienced legal assistance, and clear pricing starting at $45 plus official filing charges.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => openModal('registration')}
                className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-10 py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#d29b00]/30"
              >
                Register My Trademark
              </button>
              <AttorneyChatButton />
            </div>

            <RegistrationModal 
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              initialType={modalType}
              packageTitle="Basic"
              packageAmount="45"
            />

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8">
              {/* Trustpilot */}
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 fill-[#168356]" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white">Excellent <CounterAnimation end={4.9} decimals={1} />/5</div>
                  <div className="text-xs text-gray-400"><CounterAnimation end={18543} /> reviews</div>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6">
                <div className="text-center bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                  <div className="text-xl font-bold text-[#168356]"><CounterAnimation end={98} suffix="%" /></div>
                  <div className="text-xs text-gray-400">Success Rate</div>
                </div>
                <div className="text-center bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                  <div className="text-xl font-bold text-[#168356]">24-48h</div>
                  <div className="text-xs text-gray-400">Fast Filing</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gradient Wave */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#168356] via-[#0a5938] to-[#168356] animate-pulse"></div>
      </div>

      {/* Services Bar */}
      <div className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <div key={index} className="flex items-center gap-3 group cursor-pointer">
                <div className="flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <service.icon className="w-6 h-6 text-[#168356]" />
                </div>
                <span className="text-sm font-medium group-hover:text-[#168356] transition-colors">{service.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;